import type { ZodSchema, ZodError } from 'zod';
import { ToastService } from './Toast';
import { enhanceSearchableSelects } from './SearchableSelect';
import { escapeHtml } from '@shared/utils/sanitize';
import { fmtDate, parseFlexibleDateToIso } from '@shared/utils/fmtDate';

function attrEscape(value: string | number): string {
  return String(value).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}

const DATE_PAIRS: Array<[string, string]> = [
  ['date_debut', 'date_fin'],
  ['date_embauche', 'date_fin_contrat'],
  ['date', 'date_echeance'],
  ['date_facture', 'date_echeance'],
  ['date_naissance', 'date_embauche'],
];

function isoToday(): string {
  return new Date().toISOString().slice(0, 10);
}

function displayDateValue(raw: string | number | undefined): string {
  if (raw === undefined || raw === '') return '';
  const s = String(raw);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return fmtDate(s);
  return s;
}

/**
 * Helper de gestion de formulaire avec validation Zod.
 */
export class FormHelper {
  static clearFieldErrors(form: HTMLFormElement): void {
    form.querySelectorAll('.field-error').forEach((el) => el.remove());
    form.querySelectorAll('.is-invalid').forEach((el) => {
      el.classList.remove('is-invalid');
      el.removeAttribute('aria-invalid');
      if (el instanceof HTMLElement && el.hasAttribute('aria-describedby')) {
        const described = el.getAttribute('aria-describedby') ?? '';
        if (described.includes('-error')) el.removeAttribute('aria-describedby');
      }
    });
    FormHelper.clearErrorSummary(form);
  }

  /**
   * Phase3 T-02 — réinitialise le formulaire après une création réussie
   * (équivalent vanilla de `form.reset()` React Hook Form).
   * Restaure les valeurs HTML initiales, sync dates / listes filtrables, efface les erreurs.
   */
  static resetForm(form: HTMLFormElement): void {
    FormHelper.clearFieldErrors(form);
    form.reset();
    form.querySelectorAll<HTMLInputElement>('.nx-date-text').forEach((text) => {
      const native = form.querySelector<HTMLInputElement>(
        `.nx-date-native[data-for="${CSS.escape(text.name)}"]`,
      );
      if (native) native.value = parseFlexibleDateToIso(text.value) ?? '';
    });
    form.querySelectorAll<HTMLSelectElement>('select').forEach((sel) => {
      sel.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /** Zone résumé d'erreurs (WCAG-B-02 / G3) — role=alert pour lecteurs d'écran. */
  static ensureErrorSummary(form: HTMLFormElement): HTMLElement {
    let summary = form.querySelector<HTMLElement>('.form-error-summary');
    if (!summary) {
      summary = document.createElement('div');
      summary.className = 'form-error-summary';
      summary.id = form.id ? `${form.id}-error-summary` : 'form-error-summary';
      summary.setAttribute('role', 'alert');
      summary.setAttribute('aria-live', 'assertive');
      summary.setAttribute('aria-atomic', 'true');
      summary.hidden = true;
      form.prepend(summary);
    }
    return summary;
  }

  static clearErrorSummary(form: HTMLFormElement): void {
    form.querySelectorAll<HTMLElement>('.form-error-summary').forEach((el) => {
      el.textContent = '';
      el.hidden = true;
    });
  }

  /** Annonce un résumé d'erreurs Zod au submit (en plus des messages par champ). */
  static announceFormErrors(form: HTMLFormElement, message: string): void {
    const summary = FormHelper.ensureErrorSummary(form);
    summary.hidden = false;
    summary.textContent = '';
    summary.textContent = message;
  }

  /** W-09 / G3 — erreur liée au champ via aria-describedby + role=alert. */
  static markFieldError(form: HTMLFormElement, path: string, message: string): void {
    const name = String(path.split('.')[0] ?? '');
    if (!name) return;
    const control = form.querySelector<HTMLElement>(`[name="${name}"]`);
    if (!control) return;
    control.classList.add('is-invalid');
    const wrap = control.closest('.form-field') ?? control.parentElement;
    if (!wrap) return;
    wrap.classList.add('is-invalid');
    wrap.querySelector('.nx-combo-trigger')?.classList.add('is-invalid');
    if (wrap.querySelector(':scope > .field-error')) return;
    const errId = `${control.id || name}-error`;
    const err = document.createElement('span');
    err.className = 'field-error';
    err.id = errId;
    err.setAttribute('role', 'alert');
    err.textContent = message;
    control.setAttribute('aria-describedby', errId);
    control.setAttribute('aria-invalid', 'true');
    wrap.appendChild(err);
  }

  /** Relie les dates liées (début/fin) et borne naissance / embauche à aujourd'hui. */
  static wireDateConstraints(form: HTMLFormElement): void {
    const textInput = (name: string): HTMLInputElement | null =>
      form.querySelector<HTMLInputElement>(`[name="${name}"].nx-date-text`);
    const nativeInput = (name: string): HTMLInputElement | null =>
      form.querySelector<HTMLInputElement>(`.nx-date-native[data-for="${name}"]`);
    const legacyDate = (name: string): HTMLInputElement | null =>
      form.querySelector<HTMLInputElement>(`[name="${name}"][type="date"]`);

    const resolveIso = (el: HTMLInputElement | null): string => {
      if (!el) return '';
      if (el.classList.contains('nx-date-text')) {
        return parseFlexibleDateToIso(el.value) ?? '';
      }
      return el.value;
    };

    const applyMaxToday = (name: string): void => {
      const text = textInput(name);
      const native = nativeInput(name);
      const legacy = legacyDate(name);
      const today = isoToday();
      if (native) native.max = today;
      if (legacy && legacy.type === 'date') legacy.max = today;
      void text;
    };
    applyMaxToday('date_naissance');
    applyMaxToday('date_embauche');
    applyMaxToday('date_deces_conjoint');

    form.querySelectorAll<HTMLInputElement>('.nx-date-text').forEach((text) => {
      const name = text.name;
      const native = nativeInput(name);
      if (!native) return;

      const syncTextFromNative = (): void => {
        if (!native.value) return;
        text.value = fmtDate(native.value);
      };
      const syncNativeFromText = (): void => {
        const iso = parseFlexibleDateToIso(text.value);
        native.value = iso ?? '';
      };

      text.addEventListener('blur', syncNativeFromText);
      text.addEventListener('change', syncNativeFromText);
      native.addEventListener('change', syncTextFromNative);
      syncNativeFromText();
    });

    for (const [startName, endName] of DATE_PAIRS) {
      const start = textInput(startName) ?? legacyDate(startName);
      const end = textInput(endName) ?? legacyDate(endName);
      if (!start || !end) continue;
      const startNative = nativeInput(startName);
      const endNative = nativeInput(endName);

      const sync = (): void => {
        const startIso = resolveIso(start);
        const endIso = resolveIso(end);
        const today = isoToday();
        if (startNative) {
          if (endIso) startNative.max = endIso;
          else if (startName === 'date_naissance' || startName === 'date_embauche')
            startNative.max = today;
          else startNative.removeAttribute('max');
        } else if (start.type === 'date') {
          if (endIso) start.max = endIso;
          else if (startName === 'date_naissance' || startName === 'date_embauche')
            start.max = today;
          else start.removeAttribute('max');
        }
        if (endNative) {
          if (startIso) endNative.min = startIso;
          else endNative.removeAttribute('min');
        } else if (end.type === 'date') {
          if (startIso) end.min = startIso;
          else end.removeAttribute('min');
        }
      };
      start.addEventListener('change', sync);
      end.addEventListener('change', sync);
      start.addEventListener('blur', sync);
      end.addEventListener('blur', sync);
      sync();
    }
  }

  /** Active l’onglet contenant le premier champ en erreur et y place le focus. */
  static focusFirstInvalid(form: HTMLFormElement): HTMLElement | null {
    const control = form.querySelector<HTMLElement>('.is-invalid[name], [name].is-invalid');
    if (!control) return null;
    const panel = control.closest<HTMLElement>('.tab-content');
    if (panel?.id) {
      const root = form;
      root
        .querySelectorAll<HTMLButtonElement>('.salarie-form-tabs .tab, .tabs .tab')
        .forEach((t) => {
          t.classList.toggle('active', t.dataset.tab === panel.id);
        });
      root.querySelectorAll<HTMLElement>('.tab-content').forEach((p) => {
        p.classList.toggle('active', p.id === panel.id);
      });
    }
    control.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (typeof (control as HTMLInputElement).focus === 'function') {
      (control as HTMLInputElement).focus();
    }
    return control;
  }

  /** Normalise les champs date (JJ/MM/AAAA → YYYY-MM-DD) avant validation Zod. */
  static normalizeDateFields(raw: Record<string, unknown>, form: HTMLFormElement): void {
    form.querySelectorAll<HTMLInputElement>('.nx-date-text, input[type="date"]').forEach((inp) => {
      const name = inp.name;
      if (!name) return;
      const value = raw[name];
      if (typeof value !== 'string' || value.trim() === '') return;
      if (inp.type === 'date' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return;
      const iso = parseFlexibleDateToIso(value);
      if (iso) {
        raw[name] = iso;
      } else {
        raw[name] = value;
      }
    });
  }

  /** Bind un form à un handler validé : extrait FormData, parse Zod, exécute callback. */
  static bindSubmit<S extends ZodSchema>(
    form: HTMLFormElement,
    schema: S,
    handler: (data: import('zod').infer<S>) => Promise<void> | void,
  ): void {
    enhanceSearchableSelects(form);
    FormHelper.wireDateConstraints(form);
    form.setAttribute('novalidate', 'novalidate');
    // Efface l’erreur dès que l’utilisateur corrige (évite message « requis » figé — WARN-03).
    form.addEventListener('input', (e) => {
      const t = e.target as HTMLElement | null;
      if (!t || !('name' in t) || !(t as HTMLInputElement).name) return;
      const wrap = t.closest('.form-field');
      if (!wrap?.classList.contains('is-invalid')) return;
      wrap.classList.remove('is-invalid');
      wrap.querySelector(':scope > .field-error')?.remove();
      t.classList.remove('is-invalid');
      t.removeAttribute('aria-invalid');
      if (t.getAttribute('aria-describedby')?.includes('-error')) {
        t.removeAttribute('aria-describedby');
      }
      FormHelper.clearErrorSummary(form);
    });
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      FormHelper.clearFieldErrors(form);
      const submitBtn = form.querySelector<HTMLButtonElement>('button[type="submit"]');
      const prevDisabled = submitBtn?.disabled ?? false;
      const prevHtml = submitBtn?.innerHTML;
      if (submitBtn) {
        submitBtn.disabled = true;
        if (!submitBtn.querySelector('.nx-spinner')) {
          submitBtn.dataset.nxPrevLabel = submitBtn.textContent ?? '';
          submitBtn.innerHTML = `<span class="nx-spinner" aria-hidden="true"></span> Enregistrement…`;
        }
      }
      const fd = new FormData(form);
      const raw: Record<string, unknown> = {};
      fd.forEach((v, k) => {
        if (raw[k] !== undefined) {
          const existing = raw[k];
          raw[k] = Array.isArray(existing) ? [...(existing as unknown[]), v] : [existing, v];
        } else {
          raw[k] = v;
        }
      });
      form.querySelectorAll<HTMLInputElement>('input[type=number]').forEach((inp) => {
        const v = raw[inp.name];
        if (typeof v === 'string' && v !== '') {
          const n = Number(v);
          raw[inp.name] = Number.isFinite(n) ? n : v;
        }
      });
      FormHelper.normalizeDateFields(raw, form);
      try {
        const parsed = schema.parse(raw) as import('zod').infer<S>;
        await handler(parsed);
      } catch (err) {
        if ((err as ZodError).issues) {
          const issues = (err as ZodError).issues;
          for (const issue of issues) {
            FormHelper.markFieldError(form, issue.path.join('.'), issue.message);
          }
          FormHelper.focusFirstInvalid(form);
          const first = issues[0];
          const count = issues.length;
          const summaryMsg =
            count > 1
              ? `${count} erreurs à corriger. Première : ${first?.message ?? 'Saisie invalide'}`
              : (first?.message ?? 'Saisie invalide');
          FormHelper.announceFormErrors(form, summaryMsg);
          ToastService.error(
            count > 1
              ? `${first?.message ?? 'Saisie invalide'} (${count} erreurs à corriger)`
              : (first?.message ?? 'Saisie invalide'),
          );
        } else {
          ToastService.error(String((err as Error).message ?? err));
        }
      } finally {
        if (submitBtn) {
          submitBtn.disabled = prevDisabled;
          if (prevHtml !== undefined) submitBtn.innerHTML = prevHtml;
        }
      }
    });
  }

  /** Construit un champ <input> avec label. Les dates sont saisissables en JJ/MM/AAAA. */
  static field(
    name: string,
    label: string,
    type: string = 'text',
    opts: {
      required?: boolean;
      min?: number | string;
      max?: number | string;
      maxlength?: number;
      pattern?: string;
      inputmode?: string;
      placeholder?: string;
      value?: string | number;
      autocomplete?: string;
      readonly?: boolean;
      /** id HTML optionnel (ex. libellé dynamique). */
      id?: string;
    } = {},
  ): string {
    if (type === 'date') {
      return FormHelper.dateField(name, label, opts);
    }
    const value = opts.value !== undefined ? `value="${attrEscape(opts.value)}"` : '';
    const required = opts.required ? 'required' : '';
    const readonly = opts.readonly ? 'readonly aria-readonly="true"' : '';
    const min = opts.min !== undefined ? `min="${opts.min}"` : '';
    const max = opts.max !== undefined ? `max="${opts.max}"` : '';
    const maxlength = opts.maxlength !== undefined ? `maxlength="${opts.maxlength}"` : '';
    const pattern = opts.pattern ? `pattern="${attrEscape(opts.pattern)}"` : '';
    const inputmode = opts.inputmode ? `inputmode="${attrEscape(opts.inputmode)}"` : '';
    const placeholder = opts.placeholder ? `placeholder="${attrEscape(opts.placeholder)}"` : '';
    const autocomplete = opts.autocomplete ? `autocomplete="${attrEscape(opts.autocomplete)}"` : '';
    const fieldId = opts.id ?? `field-${name}`;
    const ariaReq = opts.required ? 'aria-required="true"' : '';
    return `
      <div class="form-field" data-field="${attrEscape(name)}" style="margin-bottom:12px;">
        <label for="${attrEscape(fieldId)}" id="${attrEscape(name)}-label" style="display:block;font-size:0.8rem;color:#586069;margin-bottom:4px;">${label}${opts.required ? ' *' : ''}</label>
        <input id="${attrEscape(fieldId)}" name="${attrEscape(name)}" type="${type}" ${required} ${ariaReq} ${readonly} ${min} ${max} ${maxlength} ${pattern} ${inputmode} ${placeholder} ${value} ${autocomplete}
          style="width:100%;padding:8px 10px;border:1px solid #e1e4e8;border-radius:4px;font:inherit;box-sizing:border-box;${opts.readonly ? 'background:#f6f8fa;color:#586069;' : ''}" />
      </div>
    `;
  }

  /** Champ date : saisie libre JJ/MM/AAAA + calendrier natif (sans roulette lente seule). */
  static dateField(
    name: string,
    label: string,
    opts: {
      required?: boolean;
      min?: number | string;
      max?: number | string;
      placeholder?: string;
      value?: string | number;
      autocomplete?: string;
      readonly?: boolean;
    } = {},
  ): string {
    const display = displayDateValue(opts.value);
    const iso = parseFlexibleDateToIso(String(opts.value ?? '')) ?? '';
    const required = opts.required ? 'required' : '';
    const readonly = opts.readonly ? 'readonly aria-readonly="true"' : '';
    const min = opts.min !== undefined ? `min="${attrEscape(opts.min)}"` : '';
    const max = opts.max !== undefined ? `max="${attrEscape(opts.max)}"` : '';
    const placeholder = `placeholder="${attrEscape(opts.placeholder ?? 'JJ/MM/AAAA')}"`;
    const autocomplete = opts.autocomplete
      ? `autocomplete="${attrEscape(opts.autocomplete)}"`
      : name === 'date_naissance'
        ? 'autocomplete="bday"'
        : '';
    return `
      <div class="form-field nx-date-field" data-field="${attrEscape(name)}" style="margin-bottom:12px;">
        <label style="display:block;font-size:0.8rem;color:#586069;margin-bottom:4px;">${label}${opts.required ? ' *' : ''}</label>
        <div style="display:flex;gap:6px;align-items:stretch;">
          <input name="${attrEscape(name)}" type="text" class="nx-date-text" inputmode="numeric"
            ${required} ${readonly} ${placeholder} ${autocomplete}
            value="${attrEscape(display)}"
            style="flex:1;min-width:0;padding:8px 10px;border:1px solid #e1e4e8;border-radius:4px;font:inherit;box-sizing:border-box;${opts.readonly ? 'background:#f6f8fa;color:#586069;' : ''}" />
          <input type="date" class="nx-date-native" data-for="${attrEscape(name)}"
            ${min} ${max} value="${attrEscape(iso)}"
            aria-label="Choisir ${attrEscape(label)} dans le calendrier"
            style="width:42px;padding:4px;border:1px solid #e1e4e8;border-radius:4px;background:#fff;cursor:pointer;flex-shrink:0;"
            ${opts.readonly ? 'disabled' : ''} />
        </div>
        <span style="display:block;font-size:0.72rem;color:#6a737d;margin-top:4px;">Saisie libre (JJ/MM/AAAA) ou calendrier</span>
      </div>
    `;
  }

  /** Construit un <textarea> multiligne. */
  static textarea(
    name: string,
    label: string,
    opts: { required?: boolean; placeholder?: string; value?: string; rows?: number } = {},
  ): string {
    const required = opts.required ? 'required' : '';
    const placeholder = opts.placeholder ? `placeholder="${attrEscape(opts.placeholder)}"` : '';
    const rows = opts.rows ?? 3;
    const value = opts.value ? attrEscape(opts.value) : '';
    return `
      <div class="form-field" data-field="${attrEscape(name)}" style="margin-bottom:12px;">
        <label style="display:block;font-size:0.8rem;color:#586069;margin-bottom:4px;">${label}${opts.required ? ' *' : ''}</label>
        <textarea name="${attrEscape(name)}" rows="${rows}" ${required} ${placeholder}
          style="width:100%;padding:8px 10px;border:1px solid #e1e4e8;border-radius:4px;font:inherit;box-sizing:border-box;resize:vertical;">${value}</textarea>
      </div>
    `;
  }

  /** Construit un <select> (ensuite enrichi en liste filtrable). */
  static select(
    name: string,
    label: string,
    options: { value: string; label: string }[],
    opts: { required?: boolean; value?: string; placeholder?: string } = {},
  ): string {
    const placeholder = opts.placeholder
      ? `<option value="">${attrEscape(opts.placeholder)}</option>`
      : '';
    return `
      <div class="form-field" data-field="${attrEscape(name)}" style="margin-bottom:12px;">
        <label style="display:block;font-size:0.8rem;color:#586069;margin-bottom:4px;">${label}${opts.required ? ' *' : ''}</label>
        <select name="${attrEscape(name)}" class="nx-select" ${opts.required ? 'required' : ''} ${opts.placeholder ? `data-placeholder="${attrEscape(opts.placeholder)}"` : ''}>
          ${placeholder}
          ${options.map((o) => `<option value="${attrEscape(o.value)}" ${o.value === opts.value ? 'selected' : ''}>${escapeHtml(o.label)}</option>`).join('')}
        </select>
      </div>
    `;
  }
}
